import React from 'react';
import type { ApolloClient } from '@apollo/client';

import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Button } from '@oysterjs/ui/button';
import { DataTable, type ColumnDef, type SortDirection } from '@oysterjs/ui/data-table';
import {
  type ActiveFilter,
  type FilterDefinition,
  SearchFilterInput
} from '@oysterjs/ui/search-filter';

import { evictConnectionField } from '~/api/cache';
import { ConnectionLoadMoreFooter } from '~/components/connection-load-more-footer';
import { FormError } from '~/components/form-error';
import { ClientTabEmptyState, ClientTabLoader, useBulkAction } from './client-panels';

/**
 * Every client tab reads its first page from the cache its route loader filled;
 * a filter or sort change is new variables, and so a request of its own.
 */
export const CLIENT_TAB_QUERY_OPTIONS = {
  fetchPolicy: 'cache-first',
  notifyOnNetworkStatusChange: true
} as const;

type Connection<T> = {
  edges: { node: T }[];
  pageInfo: { hasNextPage: boolean; endCursor?: string | null };
  totalCount?: number;
};

/** The parts of a generated query hook's result the list uses. */
type ListQuery<V> = {
  loading: boolean;
  fetchMore: (options: { variables: V & { after: string } }) => Promise<unknown>;
  refetch: () => Promise<unknown>;
  client?: ApolloClient<unknown>;
};

/**
 * The cached field a tab's rows live in: a Client's connection, or a Query
 * field when `owner` is absent.
 */
export type ClientListField = { owner?: { __typename: string; id: string }; fieldName: string };

/**
 * A client tab's paged rows, selection and bulk actions over one connection.
 *
 * The cache keeps a slot per filter and sort, and the tab reads cache-first, so
 * refetching the slot on screen after a mutation would leave every other slot
 * listing what was just removed. `refresh` evicts the whole field instead.
 */
export const useClientConnection = <T extends { id: string }, V extends object>(
  query: ListQuery<V>,
  connection: Connection<T> | null | undefined,
  variables: V,
  field: ClientListField
) => {
  const rows = React.useMemo(
    () => (connection?.edges ?? []).map((edge) => edge.node),
    [connection?.edges]
  );
  const hasNextPage = connection?.pageInfo.hasNextPage ?? false;
  const endCursor = connection?.pageInfo.endCursor ?? null;

  const { fetchMore, refetch, client } = query;
  const loadMore = React.useCallback(() => {
    if (!endCursor || !hasNextPage) return;
    fetchMore({ variables: { ...variables, after: endCursor } });
  }, [endCursor, fetchMore, hasNextPage, variables]);

  const { owner, fieldName } = field;
  const ownerTypename = owner?.__typename;
  const ownerId = owner?.id;
  const refresh = React.useCallback(async () => {
    if (client?.cache) {
      evictConnectionField(
        client.cache,
        fieldName,
        ownerTypename && ownerId ? { __typename: ownerTypename, id: ownerId } : undefined
      );
    }
    await refetch();
  }, [client, fieldName, ownerId, ownerTypename, refetch]);

  const [selected, setSelected] = React.useState<string[]>([]);
  const selectedSet = React.useMemo(() => new Set(selected), [selected]);
  // A selection made under one filter would otherwise act on rows the new one hides.
  React.useEffect(() => setSelected([]), [variables]);

  const bulk = useBulkAction(refresh);

  return {
    rows,
    loading: query.loading,
    hasNextPage,
    totalCount: connection?.totalCount,
    loadMore,
    refresh,
    selected,
    setSelected,
    selectedSet,
    bulk
  };
};

export type ClientConnection<T extends { id: string }> = ReturnType<
  typeof useClientConnection<T, object>
>;

/** Failure copy for removing rows from a client; `noun` is singular and takes an s. */
export const removeFailed = (noun: string) => (count: number) =>
  count === 1
    ? `Could not remove one ${noun}. It is still on this client.`
    : `Could not remove ${count} ${noun}s. They are still on this client.`;

/** ArchiveConfirmDialog copy for removing rows from a client; `kept` says where they remain. */
export const removeConfirmCopy = (noun: string, count: number, kept: string) => {
  const nouns = count === 1 ? noun : `${noun}s`;
  return {
    title: `Remove ${nouns}`,
    confirmLabel: 'Remove',
    description: (
      <>
        Remove <strong>{count}</strong> {nouns} from this client? {kept}
      </>
    )
  };
};

/**
 * The body every client tab shares: a filter box with the tab's primary action
 * beside it, then the table, its empty state or its loading card. Dialogs and
 * the selection bar go in `children`.
 */
export const ClientListTab = <T extends { id: string }, F, S extends string>(props: {
  list: ClientConnection<T>;
  /** Plural, as the loader and footer say it: "files", "documents". */
  noun: string;
  filterDefinitions: FilterDefinition<F>[];
  onFiltersChange: (filters: ActiveFilter<F>[]) => void;
  filtered: boolean;
  placeholder: string;
  action: { label: string; onClick: () => void };
  empty: { icon: React.ComponentType<{ className?: string }>; title: string; description: string };
  columns: ColumnDef<T, S>[];
  rowHref?: (row: T) => string | undefined;
  sortBy: S;
  sortDirection: SortDirection;
  onSort: (sortBy: S, sortDirection: SortDirection) => void;
  children?: React.ReactNode;
}) => {
  const { list } = props;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1 basis-80">
          <SearchFilterInput
            filterDefinitions={props.filterDefinitions}
            onFiltersChange={props.onFiltersChange}
            placeholder={props.placeholder}
          />
        </div>
        <Button color="sky" onClick={props.action.onClick}>
          {props.action.label}
        </Button>
      </div>

      <FormError message={list.bulk.error} />

      {list.loading && list.rows.length === 0 ? (
        <ClientTabLoader noun={props.noun} />
      ) : list.rows.length === 0 ? (
        props.filtered ? (
          <ClientTabEmptyState
            icon={MagnifyingGlassIcon}
            title={`No ${props.noun} found`}
            description="Try adjusting your search or filters."
          />
        ) : (
          <ClientTabEmptyState {...props.empty} />
        )
      ) : (
        <div className="rounded-lg bg-white px-4 shadow sm:px-6 dark:bg-neutral-800">
          <DataTable
            data={list.rows}
            columns={props.columns}
            keyExtractor={(row) => row.id}
            rowHref={props.rowHref}
            tableClassName="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
            sortKey={props.sortBy}
            sortDirection={props.sortDirection}
            onSort={props.onSort}
            loading={list.loading}
          />
          <ConnectionLoadMoreFooter
            shown={list.rows.length}
            totalCount={list.totalCount}
            noun={props.noun}
            hasNextPage={list.hasNextPage}
            onLoadMore={list.loadMore}
            loading={list.loading}
          />
        </div>
      )}

      {props.children}
    </div>
  );
};
